/*
 * WB custom: make off-white product photo backgrounds display as pure white on product cards.
 *
 * Some product photos (usually JPGs) have a light grey / off-white background baked into the
 * image (e.g. RGB 247,248,243), so they look grey next to photos with a true white background.
 * Transparent PNGs are already shown on white by CSS; this handles the photos CSS can't fix.
 *
 * For each product card image, a small copy is loaded and its four corners are sampled. If all
 * four are a near-white neutral colour (and not already white), the card image is brightened just
 * enough to turn that background white (at most 12%). Other images are left untouched.
 */
(() => {
  const SELECTOR = '.product-card__media img, .product-recommendations__item-media img, .card-product-small img, .template--collection .card-collection__media img';
  const MIN_CHANNEL = 225;   // corners lighter than this count as "near white"
  const MAX_TINT = 14;       // corners must be close to neutral grey (max - min channel)
  const MAX_FACTOR = 1.12;   // never brighten more than 12%
  const SIZE = 48;
  const cache = new Map();   // image URL (no query) -> Promise<brightness factor>

  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });

  const baseUrl = img => {
    let src = img.currentSrc || img.src || '';
    if (!src || src.startsWith('data:')) return '';
    if (src.startsWith('//')) src = window.location.protocol + src;
    return src.split('?')[0];
  };

  const measure = url =>
    new Promise(resolve => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        try {
          ctx.clearRect(0, 0, SIZE, SIZE);
          ctx.drawImage(probe, 0, 0, SIZE, SIZE);
          const corners = [[1, 1], [SIZE - 2, 1], [1, SIZE - 2], [SIZE - 2, SIZE - 2]].map(
            ([x, y]) => ctx.getImageData(x, y, 1, 1).data
          );
          const opaque = corners.every(c => c[3] === 255);
          const nearWhite = corners.every(c => Math.min(c[0], c[1], c[2]) >= MIN_CHANNEL);
          const neutral = corners.every(c => Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]) <= MAX_TINT);
          const alreadyWhite = corners.every(c => c[0] >= 253 && c[1] >= 253 && c[2] >= 253);
          if (opaque && nearWhite && neutral && !alreadyWhite) {
            const darkest = Math.min(...corners.map(c => Math.min(c[0], c[1], c[2])));
            resolve(Math.min(255 / darkest, MAX_FACTOR));
            return;
          }
        } catch (e) {
          // Pixels can't be read (no CORS on that image): leave it alone.
        }
        resolve(1);
      };
      probe.onerror = () => resolve(1);
      // A tiny version is enough to read the corners (Shopify CDN resizes on the fly).
      probe.src = `${url}?width=${SIZE * 2}`;
    });

  const process = img => {
    const url = baseUrl(img);
    if (!url) return;
    if (!cache.has(url)) cache.set(url, measure(url));
    cache.get(url).then(factor => {
      img.style.filter = factor > 1.001 ? `brightness(${factor.toFixed(3)})` : '';
    });
  };

  const scan = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(img => {
      if (img.complete && img.naturalWidth) process(img);
    });
  };

  // Images that load later (lazy loading, hover images, infinite scroll, filtering, variant swaps).
  document.addEventListener(
    'load',
    event => {
      const img = event.target;
      if (img instanceof HTMLImageElement && img.matches(SELECTOR)) process(img);
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }
  document.addEventListener('shopify:section:load', event => scan(event.target));
})();
